import { useCallback } from 'react';

import { useApi } from 'api/useApi';
import { ENV_VARS } from 'app/config/environment-vars';

import type { DataNode, DataNodeDetail, GetConnectedNodesParams } from './types';

const PROJECT_NODES_ERROR_KEY = 'DataApiClient';

/** Server-side filter operators. Names match the backend `ENodeFilterOperator` enum. */
export type NodeFilterOperator =
  | 'Contains'
  | 'Equals'
  | 'StartsWith'
  | 'EndsWith'
  | 'IsEmpty'
  | 'NotEquals'
  | 'GreaterThan'
  | 'LessThan'
  | 'GreaterThanOrEqual'
  | 'LessThanOrEqual'
  | 'Is'
  | 'IsAnyOf';

/** One server-side column filter (mirrors one DataGridPro filter item). Multiple are AND-combined. */
export interface NodeFilter {
  field: string;
  operator: NodeFilterOperator;
  /** Raw value(s): one for scalar operators, several for `IsAnyOf`, none for `IsEmpty`. */
  values: string[];
}

export interface GetDataNodesByTypeParams {
  offset: number;
  limit: number;
  proposalId?: string;
  orderBy?: string;
  orderDirection?: 'Asc' | 'Desc';
  filters?: NodeFilter[];
}

export function useProjectNodesApi() {
  const baseURL = `${ENV_VARS.DATA_URL}/api`;
  const fetchFunction = useApi(baseURL, PROJECT_NODES_ERROR_KEY);

  const getDataNodesByType = useCallback(
    (
      projectId: string,
      technicalName: string,
      { offset, limit, proposalId, orderBy, orderDirection, filters }: GetDataNodesByTypeParams
    ) => {
      const params = new URLSearchParams({
        technicalName,
        offset: String(offset),
        limit: String(limit)
      });
      if (proposalId) {
        params.set('proposalId', proposalId);
      }
      if (orderBy) {
        params.set('orderBy', orderBy);
      }
      if (orderDirection) {
        params.set('orderDirection', orderDirection);
      }
      if (filters && filters.length > 0) {
        // Nested list can't bind cleanly from a query string, so it travels as JSON (still a GET).
        params.set('filters', JSON.stringify(filters));
      }
      return fetchFunction<DataNode[]>(`projects/${projectId}/nodes?${params.toString()}`);
    },
    [fetchFunction]
  );

  /**
   * Fetch a single data node by id (for seeding the graph root). The caller supplies the node's
   * blueprint type (`templateNodeId`) — known from the sidebar group the node was launched from.
   */
  const getDataNodeDetail = useCallback(
    (projectId: string, dataNodeId: string, templateNodeId: string, proposalId?: string) => {
      const params = new URLSearchParams({ templateNodeId });
      if (proposalId) {
        params.set('proposalId', proposalId);
      }
      return fetchFunction<DataNodeDetail>(`projects/${projectId}/nodes/${dataNodeId}?${params.toString()}`);
    },
    [fetchFunction]
  );

  /**
   * Fetch one page of nodes connected to `parentDataNodeId` through a single blueprint relation.
   * Permission-, proposal- and pagination-aware (unlike the retired unbounded graph endpoint).
   */
  const getConnectedNodes = useCallback(
    (
      projectId: string,
      parentDataNodeId: string,
      {
        parentNodeTechnicalName,
        relationId,
        technicalName,
        isReverse,
        offset,
        limit,
        orderBy,
        orderDirection,
        proposalId
      }: GetConnectedNodesParams
    ) => {
      const params = new URLSearchParams({
        parentNodeTechnicalName,
        relationId,
        technicalName,
        isReverse: String(isReverse),
        offset: String(offset),
        limit: String(limit),
        // OrderBy is required by the backend; a stable sort key keeps offset pages from overlapping.
        orderBy: orderBy ?? 'Id',
        orderDirection: orderDirection ?? 'Asc'
      });
      if (proposalId) {
        params.set('proposalId', proposalId);
      }
      return fetchFunction<DataNode[]>(
        `projects/${projectId}/nodes/${parentDataNodeId}/connected?${params.toString()}`
      );
    },
    [fetchFunction]
  );

  return { getDataNodesByType, getDataNodeDetail, getConnectedNodes };
}
